




































import { computed, defineComponent, ref } from "@vue/composition-api";
import { PaymentPackageEnum } from "@scrile/api-provider/dist/api/PaymentPackageProvider";
import {
  InputSubscriptionLevelCreate,
  InputSubscriptionLevelUpdate,
  SubscriptionLevel,
} from "@scrile/api-provider/dist/api/SubscriptionLevelProvider";
import { UserVerificationStatus } from "@scrile/api-provider/dist/api/UserVerificationProvider";
import useAuthUser from "@/hooks/useAuthUser";
import useMySubscriptionLevels from "@/hooks/useMySubscriptionLevels";
import usePaymentPackages from "@/hooks/usePaymentPackages";
import providers from "@/lib/api";
import BaseButtonCreate from "@/components/BaseButtonCreate";
import BasePage from "@/components/BasePage";
import EmptyContent from "@/components/EmptyContent";
import PageTitle from "@/components/PageTitle";
import CreateLevelView from "./components/CreateLevelView.vue";
import EditLevelView from "./components/EditLevelView.vue";
import LevelsView from "./components/LevelsView.vue";
import NoLevelsView from "./components/NoLevelsView.vue";

export default defineComponent({
  name: "PageSubscriptionLevels",
  components: {
    EditLevelView,
    CreateLevelView,
    LevelsView,
    NoLevelsView,
    BasePage,
    PageTitle,
    BaseButtonCreate,
    EmptyContent,
  },
  setup() {
    const { paymentPackages: packageList, getPaymentPackages } = usePaymentPackages([PaymentPackageEnum.SUBSCRIPTION]);
    const { mySubscriptionLevels: priceList, getMySubscriptionLevels } = useMySubscriptionLevels();
    const { authUserStrict, userVerificationUpgradeStatus } = useAuthUser();

    const approved = ref(true);
    const showCreate = ref(false);
    const priceToEdit = ref<SubscriptionLevel | null>(null);
    const loading = ref(false);
    const saveWelcomeMessage = ref<((subjectId: string) => Promise<void>) | null>(null);

    const userId = computed(() => authUserStrict.value.id);

    const showEdit = computed(() => {
      return !!priceToEdit.value;
    });

    const closeEdit = () => {
      saveWelcomeMessage.value = null;
      priceToEdit.value = null;
    };
    const openEdit = (id: string) => {
      if (!priceList.value) return;
      priceToEdit.value = priceList.value.find(i => i.id === id) || null;
    };

    const createLevel = async (data: InputSubscriptionLevelCreate) => {
      try {
        loading.value = true;
        const { id } = await providers.SubscriptionLevelProvider.create({ data });
        await getMySubscriptionLevels(true);
        if (saveWelcomeMessage.value) {
          await saveWelcomeMessage.value(id);
        }
        showCreate.value = false;
      } finally {
        loading.value = false;
      }
    };

    const updateLevel = async (data: InputSubscriptionLevelUpdate) => {
      try {
        loading.value = true;
        const { id } = await providers.SubscriptionLevelProvider.update({ data });
        if (saveWelcomeMessage.value) {
          await saveWelcomeMessage.value(id);
        }
        await getMySubscriptionLevels(true);
        closeEdit();
      } finally {
        loading.value = false;
      }
    };

    const deleteLevel = async (id: string) => {
      await providers.SubscriptionLevelProvider.delete({ id });
      closeEdit();
      await getMySubscriptionLevels(true);
    };

    const getData = async () => {
      approved.value = userVerificationUpgradeStatus.value === UserVerificationStatus.VERIFIED;
      try {
        loading.value = true;
        await Promise.all([getPaymentPackages(), getMySubscriptionLevels()]);
      } finally {
        loading.value = false;
      }
    };

    getData();

    return {
      loading,
      userId,
      approved,
      showEdit,
      priceList,
      packageList,
      showCreate,
      saveWelcomeMessage,
      priceToEdit,
      closeEdit,
      openEdit,
      createLevel,
      updateLevel,
      deleteLevel,
    };
  },
});
